/**
 * plyxVideoCompress — iter-119q
 *
 * Browser-side video transcoder for the Plyx Duet flow. The Duet
 * upload endpoint sits behind an nginx ingress that caps request
 * bodies at ~100MB, but a typical phone video at default resolution
 * exceeds that within 15-30 seconds. Rather than rejecting users with
 * "your clip is too large", we re-encode the clip down to ~720p
 * H.264 / VP8 in the browser before uploading. A 180MB clip routinely
 * shrinks to 25-40MB after this pass.
 *
 * Strategy (MediaRecorder + canvas + video element):
 *   1. Load the source File into a hidden <video> element (muted to
 *      satisfy autoplay policies; the OriginalAudioTrack comes from
 *      `video.captureStream()` not the speaker).
 *   2. Create an <canvas> sized to ≤720x1280 (preserving aspect).
 *   3. requestAnimationFrame() the video frame onto the canvas.
 *   4. Combine `canvas.captureStream(30)` (video) with the audio
 *      track from `video.captureStream()` into a fresh MediaStream.
 *   5. Pipe it through MediaRecorder with a target bitrate of
 *      ~2.5 Mbps (more than enough for portrait phone content).
 *   6. When the video element finishes playback, stop the recorder
 *      and resolve with a new Blob.
 *
 * Compatibility:
 *   - Chrome desktop + Android: ✓ full support
 *   - Safari 17+: ✓ supports MediaRecorder with H.264 in MP4
 *   - Safari 14-16: partial — falls back to WebM via VP8
 *   - Firefox: ✓ WebM/VP8/VP9
 *   - Older browsers: rejects via isCompressionSupported()
 *
 * Caveats:
 *   - The pipeline runs the video in real-time, so compressing a
 *     30s clip takes ~30s of wall time. UX-wise this is acceptable
 *     because the alternative is "we can't upload your clip at all"
 *     — we show a progress bar so users know what's happening.
 *   - If the source has rotation metadata (very common on iPhone)
 *     the canvas pass naturally corrects the orientation: <video>
 *     plays it the right way up, so the canvas draws it the right
 *     way up.
 */

const TARGET_LONG_EDGE = 1280; // 720p in portrait = 720x1280
const TARGET_SHORT_EDGE = 720;
const TARGET_BITRATE_BPS = 2_500_000; // 2.5 Mbps — plenty for portrait phone
const TARGET_FPS = 30;

/**
 * Picks the best MediaRecorder mime type the current browser supports.
 * We prefer MP4 (H.264 + AAC) when available since the backend FFmpeg
 * pipeline transcodes anything to MP4 anyway and skipping the
 * remux saves CPU on the server. WebM is the universal fallback.
 */
const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    'video/mp4;codecs="avc1.42E01E,mp4a.40.2"', // H.264 baseline + AAC-LC
    "video/mp4",
    'video/webm;codecs="vp9,opus"',
    'video/webm;codecs="vp8,opus"',
    "video/webm",
  ];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* ignore */ }
  }
  return null;
};

export const isCompressionSupported = () => {
  if (typeof window === "undefined") return false;
  if (typeof MediaRecorder === "undefined") return false;
  if (typeof document === "undefined") return false;
  const v = document.createElement("video");
  if (typeof v.captureStream !== "function" && typeof v.mozCaptureStream !== "function") return false;
  return pickMimeType() !== null;
};

/**
 * Compress a video File to fit the upload cap.
 *
 * @param {File|Blob} file - The source video.
 * @param {object} opts
 * @param {number} [opts.maxBytes] - If the input is already under this, the
 *                                   function short-circuits and returns the
 *                                   original (no useless re-encode).
 * @param {(progress: number) => void} [opts.onProgress] - 0..1 progress callback
 *                                                        (driven by playback time / duration).
 * @returns {Promise<{file: File, originalSize: number, compressedSize: number, mime: string}>}
 */
export const compressVideoForUpload = async (file, opts = {}) => {
  const { maxBytes, onProgress } = opts;
  if (!file) throw new Error("No file given");
  if (maxBytes && file.size <= maxBytes) {
    return {
      file,
      originalSize: file.size,
      compressedSize: file.size,
      mime: file.type,
      skipped: true,
    };
  }
  if (!isCompressionSupported()) {
    throw new Error("Your browser doesn't support in-browser video compression. Try Chrome or a newer Safari.");
  }

  const mime = pickMimeType();
  const objectUrl = URL.createObjectURL(file);
  const videoEl = document.createElement("video");
  videoEl.src = objectUrl;
  videoEl.muted = true; // required for autoplay; audio still goes via captureStream
  videoEl.playsInline = true;
  videoEl.preload = "auto";
  videoEl.crossOrigin = "anonymous";

  await new Promise((resolve, reject) => {
    const onMeta = () => { videoEl.removeEventListener("loadedmetadata", onMeta); resolve(); };
    const onErr = () => reject(new Error("Could not read this video file"));
    videoEl.addEventListener("loadedmetadata", onMeta);
    videoEl.addEventListener("error", onErr);
  });

  // Compute output canvas size — preserve aspect, fit inside 720x1280.
  const sw = videoEl.videoWidth;
  const sh = videoEl.videoHeight;
  const isPortrait = sh >= sw;
  const longEdge = isPortrait ? sh : sw;
  let scale = 1;
  if (longEdge > TARGET_LONG_EDGE) scale = TARGET_LONG_EDGE / longEdge;
  const cw = Math.round(sw * scale / 2) * 2; // even for codec friendliness
  const ch = Math.round(sh * scale / 2) * 2;

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) {
    URL.revokeObjectURL(objectUrl);
    throw new Error("Canvas 2D context not available");
  }

  // Combine canvas video + original audio into one stream. Chrome's
  // captureStream() includes the video tracks of <video> by default
  // when called on the video element, but we want the canvas-scaled
  // video, so we splice them: take video from canvas, audio from
  // video element's captureStream.
  const canvasStream = canvas.captureStream(TARGET_FPS);
  let audioTracks = [];
  try {
    const vidStream = (videoEl.captureStream || videoEl.mozCaptureStream)?.call(videoEl);
    if (vidStream) audioTracks = vidStream.getAudioTracks();
  } catch { /* no audio path; we still emit a video-only clip */ }
  const combined = new MediaStream();
  canvasStream.getVideoTracks().forEach((t) => combined.addTrack(t));
  audioTracks.forEach((t) => combined.addTrack(t));

  const recorder = new MediaRecorder(combined, {
    mimeType: mime,
    videoBitsPerSecond: TARGET_BITRATE_BPS,
  });

  const chunks = [];
  recorder.addEventListener("dataavailable", (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  });

  const recorderDone = new Promise((resolve, reject) => {
    recorder.addEventListener("stop", resolve);
    recorder.addEventListener("error", (e) => reject(e?.error || new Error("MediaRecorder error")));
  });

  recorder.start(250);

  // Drive canvas paint loop + progress reporting.
  let raf = 0;
  const paint = () => {
    if (videoEl.paused || videoEl.ended) return;
    try { ctx.drawImage(videoEl, 0, 0, cw, ch); } catch { /* readback blocked — skip frame */ }
    if (videoEl.duration && Number.isFinite(videoEl.duration) && onProgress) {
      onProgress(Math.min(1, videoEl.currentTime / videoEl.duration));
    }
    raf = requestAnimationFrame(paint);
  };

  try {
    await videoEl.play();
  } catch (e) {
    URL.revokeObjectURL(objectUrl);
    try { recorder.stop(); } catch { /* ignore */ }
    throw new Error("Could not play this clip for compression. Try a shorter recording.");
  }
  paint();

  // Wait for playback to finish or hit a hard ceiling (3 min) to
  // avoid hanging on a corrupted clip.
  const playEnd = new Promise((resolve) => {
    const finish = () => { videoEl.removeEventListener("ended", finish); resolve(); };
    videoEl.addEventListener("ended", finish);
  });
  const cap = new Promise((resolve) => setTimeout(resolve, 3 * 60 * 1000));
  await Promise.race([playEnd, cap]);

  cancelAnimationFrame(raf);
  try { recorder.stop(); } catch { /* ignore */ }
  await recorderDone;
  URL.revokeObjectURL(objectUrl);

  const outBlob = new Blob(chunks, { type: mime });
  if (onProgress) onProgress(1);

  // Wrap into a File so axios FormData stays happy + the server sees a
  // sensible filename.
  const extFromMime = /mp4/i.test(mime) ? "mp4" : "webm";
  const baseName = (file.name || "duet").replace(/\.[^.]+$/, "");
  const out = new File([outBlob], `${baseName}.plyx.${extFromMime}`, { type: mime });

  return {
    file: out,
    originalSize: file.size,
    compressedSize: out.size,
    mime,
    skipped: false,
  };
};
